import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Radar } from "lucide-react";
import {
  buyerResearchApi,
  type BuyerResearchBriefReadDto,
  type BuyerResearchRunDto,
  type BuyerResearchRunSummaryDto,
  type BuyerResearchSourceLogEntryDto,
} from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { Button, EmptyState, Skeleton, StatusBadge, Timestamp } from "@/design-system/components/ui";
import { InlineQueryError } from "../queryStates";
import { AskPhoenixInput, AskPhoenixPanel, useAskPhoenixQuote } from "./AskPhoenixPanel";
import { ResearchBriefForm, ResearchBriefReadOnly } from "./ResearchBriefForm";
import { ResearchPullControl } from "./ResearchPullControl";
import { ResearchQueryTranscript } from "./ResearchQueryTranscript";
import { ResearchResults } from "./ResearchResults";
import { ResearchRunHistory } from "./ResearchRunHistory";
import { isQuickRun, QuickSearchBox, QuickSearchReadLine } from "./QuickSearchBox";
import { researchCopy } from "./researchCopy";
import { RESEARCH_PAGE_PARAM } from "./researchListFilters";
import {
  formatCost,
  formatDurationMs,
  hasSettledResults,
  isTerminalStatus,
  runStatusTone,
  scoringProgressOf,
  sourceOutcomeIsStateAbsent,
  sourceOutcomeTone,
} from "./researchFormat";
import { useCancelResearchRun, useElapsedLabel, useResearchRun, useResearchRuns } from "./useBuyerResearch";
import { useUserTimeZone } from "@/utils/datetime";

/** The URL parameter naming the selected run. Dropped by the page's `setTab`. */
export const RESEARCH_RUN_PARAM = "research";

/**
 * The Research tab — what a search found for this buyer, and the way to run
 * another.
 *
 * <p>Two states. With no runs, a plain-words explanation of what RESEARCH
 * LISTINGS does and what it costs, and the one button. With runs, the history
 * on the left and the selected run's body: while it is Queued or Running, an
 * elapsed label, a per-source progress list and a Cancel; once it is done, the
 * brief it was searched with (read-only, with each field's provenance), the
 * source log, the counts header and the candidates.</p>
 *
 * <p>The selected run rides `?research=<runId>` so a link to "that search" is
 * a link, written with `replace` like the page's other record parameters. With
 * no parameter the newest run is selected, which is the one a specialist just
 * queued.</p>
 *
 * <p>"Full research" is HIDDEN, not disabled, for a reader who cannot edit
 * the buyer — the module convention. It builds the brief first (an AI call
 * that persists nothing) and only then opens the brief form, so the specialist
 * never sees an empty form.</p>
 */
export function ResearchTab({
  buyerLeadId,
  canEdit,
  buyerDisplayName,
}: {
  buyerLeadId: string;
  canEdit: boolean;
  buyerDisplayName: string;
}) {
  const copy = researchCopy.tab;
  const [params, setParams] = useSearchParams();
  /**
   * Everything the brief form needs to open, held whole rather than unpacked: the
   * proposed brief, the two guards that can refuse a run, and any conversation
   * waiting to be resumed.
   *
   * 🔴 The endpoint returns a WRAPPER rather than three members on the brief,
   * because that record is ALSO the start-run request body — a `guards`
   * member on it would be meaningful outbound and ignored inbound.
   */
  const [briefRead, setBriefRead] = useState<BuyerResearchBriefReadDto | null>(null);
  const [briefError, setBriefError] = useState<string | null>(null);

  const runs = useResearchRuns(buyerLeadId);
  const runList = runs.data ?? [];
  const requested = params.get(RESEARCH_RUN_PARAM);
  const selectedId =
    requested && runList.some((r) => r.id === requested) ? requested : (runList[0]?.id ?? null);

  const selectRun = (runId: string) => {
    const p = new URLSearchParams(params);
    p.set(RESEARCH_RUN_PARAM, runId);
    // R20 — the candidate filters carry over to the next run; the page does
    // not, because page 3 of one run's list is nothing in particular on
    // another's.
    p.delete(RESEARCH_PAGE_PARAM);
    setParams(p, { replace: true });
  };

  // Persists nothing — it proposes a brief for the form, and the form's
  // own Run is the write. Nothing cached moves, so nothing is invalidated.
  const buildBrief = useMutation({
    // no-invalidation: the brief endpoint is a proposal; the run mutation that follows it invalidates.
    mutationFn: () => buyerResearchApi.buildBrief(buyerLeadId),
    onSuccess: (read) => {
      setBriefError(null);
      setBriefRead(read);
    },
    onError: (e: unknown) => setBriefError(`${copy.briefFailed} ${extractErrorMessage(e)}`),
  });

  const onStarted = (run: BuyerResearchRunDto) => selectRun(run.run.id);
  const onQuickStarted = (run: BuyerResearchRunSummaryDto) => selectRun(run.id);

  // RG23 — a pull goes back to page 1 once, when it starts, and leaves every
  // filter alone. After that the list refreshes in place as each group of
  // twenty is saved (RG23), and the page is never reset again: the reader may
  // have paged on, and moving them back every five seconds would take the page
  // out from under them.
  const resetListPage = () => {
    setParams(
      (prev) => {
        if (!prev.has(RESEARCH_PAGE_PARAM)) return prev;
        const p = new URLSearchParams(prev);
        p.delete(RESEARCH_PAGE_PARAM);
        return p;
      },
      { replace: true },
    );
  };

  const briefForm = briefRead ? (
    // Unmounted on close and mounted on the next proposal, so every open seeds
    // a fresh draft rather than reviving the last one's edits.
    <ResearchBriefForm
      open
      buyerLeadId={buyerLeadId}
      initialBrief={briefRead.brief}
      guards={briefRead.guards}
      resume={briefRead.resume}
      onClose={() => setBriefRead(null)}
      onStarted={onStarted}
    />
  ) : null;

  const action = canEdit ? (
    <Button
      onClick={() => buildBrief.mutate()}
      loading={buildBrief.isPending}
      data-testid="research-start"
    >
      {buildBrief.isPending ? copy.actionPending : copy.action}
    </Button>
  ) : null;

  // A1/A2 — beside FULL RESEARCH, and on the same gate: hidden, not
  // disabled, for a reader who cannot edit the buyer.
  const quickBox = canEdit ? <QuickSearchBox buyerLeadId={buyerLeadId} onStarted={onQuickStarted} /> : null;

  const briefErrorLine = briefError ? (
    <p className="m-0 text-caption text-status-error" role="alert" data-testid="research-brief-failed">
      {briefError}
    </p>
  ) : null;

  if (runs.isLoading) {
    return (
      <div className="grid gap-space-3" data-testid="research-loading">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (runs.isError || !runs.data) {
    return (
      <div data-testid="research-error">
        <InlineQueryError
          what={copy.loadFailedWhat}
          onRetry={() => void runs.refetch()}
          retrying={runs.isFetching}
        />
      </div>
    );
  }

  if (runList.length === 0) {
    return (
      <div data-testid="research-empty">
        <EmptyState
          icon={Radar}
          title={copy.emptyTitle}
          description={canEdit ? copy.emptyDescription : copy.emptyDescriptionReadOnly}
        >
          {action && (
            <div className="mt-space-4 grid gap-space-2 justify-items-center" data-testid="research-empty-actions">
              <div className="flex flex-wrap items-start justify-center gap-space-2 w-full">
                {quickBox}
                {action}
              </div>
              {briefErrorLine}
            </div>
          )}
        </EmptyState>
        {briefForm}
      </div>
    );
  }

  return (
    <div className="grid gap-space-4" data-testid="research-tab">
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <p className="m-0 text-small text-gray-mid">{copy.intro(buyerDisplayName)}</p>
        <div className="grid gap-space-1 justify-items-end" data-testid="research-header-actions">
          <div className="flex flex-wrap items-start justify-end gap-space-2">
            {quickBox}
            {action}
          </div>
          {briefErrorLine}
        </div>
      </div>

      {/* 🔴 THE 18rem COLUMN IS GONE, AND ITS BREAKPOINT IS WHY. It was
          `lg:grid-cols-[18rem_1fr]` — a VIEWPORT media query deciding the
          layout of a container that is not the viewport. Table & Tile §12 names
          that exactly: inside the buyer page's own content column the two-track
          grid applies at 1024px of WINDOW while the panel measures ~704px, so
          run history took 288px of it and the run body was left with the rest.

          Replacing it with a container query would have fixed the breakpoint
          and kept the column; collapsing it fixes the breakpoint AND buys the
          vertical the rest of this workstream is about, because the history is
          reference material a specialist consults rarely and the run body is
          what they came for. It lives on the run bar now. */}
      {selectedId && (
        <SelectedRun
          key={selectedId}
          buyerLeadId={buyerLeadId}
          runId={selectedId}
          summary={runList.find((r) => r.id === selectedId) ?? null}
          canEdit={canEdit}
          runs={runList}
          onSelect={selectRun}
          onPullStarted={resetListPage}
        />
      )}
      {briefForm}
    </div>
  );
}

/**
 * One run's body — the run bar, the account of the search, and the candidates.
 *
 * <p>🔴 <b>THREE ROWS ABOVE THE LIST, WHERE THERE WERE TWELVE BLOCKS.</b> The
 * intro row belongs to the tab; this component owns the other two. The RUN BAR
 * folds what were six blocks — the "Selected run" heading, the status badge,
 * the queued/elapsed label, the cancel affordance, the counts and the spend —
 * onto one wrapping line, and carries the run picker that replaced the 18rem
 * history column. The ACCOUNT folds the brief, the query transcript and the
 * source log behind one disclosure.</p>
 *
 * <p>🔴 <b>THE VISIBLE "Selected run" HEADING IS GONE AND THE REGION KEEPS ITS
 * NAME.</b> Deleting an `h3` normally costs a screen-reader user a landmark, so
 * the `section` carries `aria-label` instead and is still announced. What is
 * NOT lost is an outline level: the five sections beneath it — brief, query,
 * sources, Ask Phoenix, candidates — all render `h3` themselves, so "Selected
 * run" was an `h3` whose own children were `h3`s. That is a flat outline
 * claiming a nesting it does not have, and removing the parent heading removes
 * the claim rather than deepening it. The pairing argument that used to sit
 * here — that this heading and "Run history" next door had to move together —
 * was about PROMOTING the treatment to SS17.2's ladder, and it is moot: there
 * is no longer a heading here to promote, and "Run history" now labels a list
 * inside a disclosure, which is what a caption-weight heading is for.</p>
 *
 * <p>Polls while the run is in flight; the hook stops on its own once the
 * status is terminal.</p>
 */
function SelectedRun({
  buyerLeadId,
  runId,
  summary,
  canEdit,
  runs,
  onSelect,
  onPullStarted,
}: {
  buyerLeadId: string;
  runId: string;
  /** The list's own row, so the bar renders before the detail arrives. */
  summary: BuyerResearchRunSummaryDto | null;
  canEdit: boolean;
  /** Every run for this buyer, newest first — the picker's list. */
  runs: BuyerResearchRunSummaryDto[];
  onSelect: (runId: string) => void;
  /** A pull was queued from this run: put the list back on page 1 (RG23). */
  onPullStarted: () => void;
}) {
  const copy = researchCopy.tab;
  const tz = useUserTimeZone();
  const detail = useResearchRun(buyerLeadId, runId);
  const run = detail.data?.run ?? summary;

  // 🔴 A run that FINISHED and is now doing more work on what it found —
  // `Pulling` a further page, or `Refining` what is on screen — keeps its
  // results, its brief and its transcript. Only a FIRST run has nothing to
  // show, and only a first run's bar means anything: `startedAt` on a refining
  // run is days old, so the elapsed label would read "Running for 72h" over a
  // refinement that started ten seconds ago, and Cancel would offer to cancel a
  // run that stopped on Tuesday.
  //
  // This replaced an `isTerminalStatus` test, which answered a DIFFERENT
  // question — "may this run still change?" — and was right only while every
  // non-terminal run was also a first run. `Pulling` and `Refining` are both
  // non-terminal AND settled, so the two answers came apart.
  const settled = run != null && hasSettledResults(run.status);
  const working = run != null && !settled;
  // A quick run has no brief and no conversation behind it: its account opens
  // with how the server read the sentence, and Ask Phoenix and the brief match
  // are not offered (A7).
  const quick = isQuickRun(run);

  // 🔴 RG23 — A FIRST RUN SHOWS ITS LIST FROM THE FIRST SAVED GROUP OF TWENTY,
  // not from the end of the run. The worker saves each group the moment it is
  // scored, and `shownCount` moves with each save, so the list replaces the
  // progress block once there is a row to put on it. Keyed on rows SHOWN rather
  // than on groups done: a first group that failed moves the counter and shows
  // nothing, and an empty table would read as "found nothing" to a reader
  // watching the rest arrive.
  //
  // `settled` is untouched and still decides everything that needs a FINISHED
  // search — the brief, the query transcript, Ask Phoenix, the pull control.
  const showResults = run != null && (settled || run.shownCount > 0);
  // Refreshed on the run's own five-second poll while the run can still change,
  // first run and pull alike, so each group lands without a refresh.
  const liveList = run != null && !isTerminalStatus(run.status);
  // The progress line of a first run, and of a confirmed Ask Phoenix re-query
  // scoring the page it bought. A pull says the same sentence in its own
  // control, at the foot of the list it is adding to.
  const scoring =
    run != null && (run.status === "Running" || run.status === "Refining") ? scoringProgressOf(run) : null;
  const elapsed = useElapsedLabel(run?.startedAt ?? run?.queuedAt, working);
  const cancel = useCancelResearchRun(buyerLeadId);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // The picker's own state. Nothing closes it explicitly: picking a row calls
  // `onSelect`, which moves `?research=`, which re-keys this component — so the
  // list closes because the thing holding it open no longer exists.
  const [pickerOpen, setPickerOpen] = useState(false);

  const sourceLog = detail.data?.sourceLog ?? [];
  const failedSources = sourceLog.filter((e) => e.outcome === "failed").length;

  // 🔴 OPEN BY DEFAULT IN EXACTLY TWO STATES, AND BOTH ARE FORCED RATHER THAN
  // CHOSEN. While the run is WORKING the source log is the progress list and
  // the only thing on screen, so collapsing it would leave a specialist
  // watching an empty panel. And a run with a FAILED source is the recorded
  // owner ruling: CompletedPartial's only account of itself is the per-source
  // error, which lives in that log, so it must be met before the results
  // rather than hidden behind a control the reader has no reason to press.
  //
  // Derived on every render rather than seeded into `useState`, because a run
  // polls Running → CompletedPartial WITHOUT remounting: a `defaultOpen`
  // computed once at mount cannot re-open for a failure that arrives later.
  // `accountOpen` is the override a specialist sets by hand, and null means
  // "whatever the run's own state says".
  const [accountOpen, setAccountOpen] = useState<boolean | null>(null);

  // WS6 put Ask Phoenix's two halves on opposite sides of the list, so the
  // quote the input takes at submit has to outlive both of them. It is held by
  // the RUN, which is keyed on the run id — so it cannot carry from one run to
  // another, which is the one thing a stale quote must never do.
  const askPhoenixQuote = useAskPhoenixQuote();
  // RG23 — the account stops being forced open once the list is on screen:
  // until then it IS the progress block, and after that the list is.
  const accountForcedOpen = (working && !showResults) || failedSources > 0;
  const accountIsOpen = accountOpen ?? accountForcedOpen;

  if (!run) {
    return <Skeleton className="h-40 w-full" />;
  }

  const selectedIndex = runs.findIndex((r) => r.id === runId);
  const pickerPosition = copy.runPickerPosition(
    selectedIndex >= 0 ? selectedIndex + 1 : 1,
    Math.max(runs.length, 1),
  );

  const accountHasBody = settled || sourceLog.length > 0;

  return (
    <section
      className="grid gap-space-4"
      aria-label={copy.selectedHeading}
      data-testid="research-selected-run"
      data-status={run.status}
    >
      {/* ── ROW 2: THE RUN BAR ──────────────────────────────────────────── */}
      <div className="grid gap-space-2">
        <div
          className="flex flex-wrap items-center gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
          data-testid="research-run-bar"
        >
          {/* \U0001F534 OFFERED ON A SINGLE RUN TOO, deliberately. "One run is not a
              choice" is a true sentence about CHOOSING and the wrong rule here:
              the history row carries facts this bar does not \u2014 who requested
              the run, sources answered of attempted, the Clef records the
              allowance was spent on, and what Clef's free count call said the
              filter matched \u2014 so hiding the disclosure on a one-run buyer would
              make those unreachable rather than tidy. The POSITION is what gets
              suppressed, because a position is a fact about a choice.

              The action rides an `sr-only` span rather than `aria-label`, which
              would replace the timestamp as the accessible name and cost a
              screen-reader user the one fact the control shows. */}
          <Button
            type="button"
            variant="text"
            size="sm"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((open) => !open)}
            data-testid="research-run-picker"
          >
            <Timestamp value={run.queuedAt} tz={tz} className="text-small text-onyx" />
            {runs.length > 1 && (
              <span className="text-caption text-gray-mid">{pickerPosition}</span>
            )}
            {pickerOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
            <span className="sr-only">{pickerOpen ? copy.runPickerHide : copy.runPickerShow}</span>
          </Button>

          <StatusBadge tone={runStatusTone(run.status)} data-testid="research-run-status">
            {researchCopy.status[run.status] ?? run.status}
          </StatusBadge>

          {settled && (
            <>
              {/* The run's counts moved to the head of the candidate list
                  (R18): the live dismissed count comes from the list read, and
                  this bar reads the run.

                  🔴 THE ONE SPEND STATEMENT, and it is above the list because the
                  reader who spent the money is the one who could not see the
                  number: what a run had cost was readable off the history rail,
                  or summed out of the per-source rows, or off the pull control at
                  the FOOT of the page — which renders nothing at all until a pull
                  is available. This is the run's own accumulated spend, correct
                  from the moment it settles and before anything is pulled.

                  What is LEFT to spend stays with the control that offers to
                  spend it (RC2). A budget above the list would be a second answer
                  to a question the pull control already owns. */}
              <span
                className="text-caption text-gray-mid tabular-nums"
                data-testid="research-run-spend"
              >
                {copy.runSpend(formatCost(run.estimatedCostUsd))}
              </span>
            </>
          )}

          {working && run.status === "Queued" && !run.startedAt && (
            <span className="text-caption text-gray-mid">{copy.queued}</span>
          )}
          {working && elapsed && (
            <span className="text-caption text-gray-mid" data-testid="research-elapsed">
              {copy.elapsedPrefix} {elapsed}
            </span>
          )}
          {run.cancelRequested && working && (
            <span className="text-caption text-gray-mid" data-testid="research-cancel-requested">
              {copy.cancelRequested}
            </span>
          )}
          {working && canEdit && !run.cancelRequested && (
            <Button
              size="sm"
              variant="secondary"
              loading={cancel.isPending}
              onClick={() => {
                setCancelError(null);
                cancel.mutate(runId, {
                  onError: (e: unknown) => setCancelError(extractErrorMessage(e)),
                });
              }}
              data-testid="research-cancel"
            >
              {cancel.isPending ? copy.cancelPending : copy.cancel}
            </Button>
          )}
        </div>

        {pickerOpen && (
          <ResearchRunHistory runs={runs} selectedId={runId} onSelect={onSelect} />
        )}

        {/* Conditional and rare, so they cost nothing on the common path and
            are still met before anything else. */}
        {cancelError && (
          <p className="m-0 text-caption text-status-error" role="alert">
            {cancelError}
          </p>
        )}
        {run.terminalReason && (
          <p className="m-0 text-caption text-gray-mid">
            {copy.terminalReasonPrefix} {run.terminalReason}
          </p>
        )}
        {run.error && <p className="m-0 text-caption text-status-error">{run.error}</p>}
      </div>

      {detail.isError && !detail.data && (
        <InlineQueryError
          what={copy.runLoadFailedWhat}
          onRetry={() => void detail.refetch()}
          retrying={detail.isFetching}
        />
      )}

      {detail.data && (
        <>
          {/* A quick run's account of itself, met BEFORE the collapsible
              account: it is the only statement of what was searched for. */}
          {quick && <QuickSearchReadLine run={run} />}

          {/* ── ROW 3: THE ACCOUNT OF THE SEARCH ────────────────────────── */}
          {accountHasBody && (
            <section className="grid gap-space-2" data-testid="research-account">
              <div className="flex flex-wrap items-center gap-space-2">
                <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
                  {copy.accountHeading}
                </h3>
                <span className="text-caption text-gray-mid break-words">
                  {settled
                    ? copy.accountSummarySettled(sourceLog.length)
                    : copy.accountSummaryWorking}
                </span>
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  aria-expanded={accountIsOpen}
                  onClick={() => setAccountOpen(!accountIsOpen)}
                  data-testid="research-account-toggle"
                >
                  {accountIsOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                  {accountIsOpen ? copy.accountHide : copy.accountShow}
                </Button>
              </div>

              {/* The failure is stated on the SUMMARY line, not only inside the
                  log, so a reader who has collapsed the account by hand is
                  still told there is something in it. */}
              {failedSources > 0 && (
                <p
                  className="m-0 text-caption text-status-error"
                  data-testid="research-account-failures"
                >
                  {copy.accountFailureNote(failedSources)}
                </p>
              )}

              {accountIsOpen && (
                <div className="grid gap-space-4">
                  {settled && !quick && (
                    <section className="grid gap-space-2">
                      {/*
                        🔴 STILL THE SS17.4 MINOR-LABEL TREATMENT, AND
                        DELIBERATELY SO UNTIL ALL FIVE MOVE TOGETHER. This is
                        one of FIVE sibling sections under the run — brief,
                        query transcript, sources, Ask Phoenix, candidates —
                        and the other three render their own headings from
                        their own files. Promoting two of five would say that
                        two of them are structure and three are field labels,
                        which is false and is the exact defect SS17.5 names: a
                        heading is structure, not emphasis.

                        And the drift here is the TREATMENT, not the tracking.
                        SS17.4's `tracking-label` fix is for a real minor label
                        — a field label, a definition-list term, the caption
                        over a figure. These are divisions of a tab body, so
                        switching them to `tracking-label` would make them
                        better-formed instances of a treatment they should not
                        be using at all; SS17.2's ladder is the fix, and it
                        belongs in a change that can edit
                        `ResearchQueryTranscript`, `AskPhoenixPanel`,
                        `ResearchResults` and `ResearchRunHistory` in the same
                        breath.
                      */}
                      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
                        {copy.briefHeading}
                      </h3>
                      {/* 🔴 D3(c) — THE DATE, NOT JUST THE HEDGE.
                          `ResearchBriefReadOnly` falls back to a dateless "as
                          recorded when this brief was written" when it is given
                          no timestamp, so a mount that omits this prop renders
                          the hedge on every real run and the date is never seen
                          by anybody. The brief is snapshotted onto the run when
                          the run is queued, so `queuedAt` IS when it was
                          written — and it is non-nullable, so there is no state
                          in which this falls back. */}
                      <ResearchBriefReadOnly
                        brief={detail.data.brief}
                        writtenAtUtc={detail.data.run.queuedAt}
                      />
                    </section>
                  )}

                  {/* Directly BELOW THE BRIEF, which is the same story told in
                      order: what we asked for, then what that became as a
                      query. SOURCES used to sit between the two, so the account
                      of the search was interrupted by a diagnostic list halfway
                      through. */}
                  {settled && !quick && (
                    <ResearchQueryTranscript
                      buyerLeadId={buyerLeadId}
                      run={detail.data.run}
                      sourceLog={detail.data.sourceLog}
                      canEdit={canEdit}
                    />
                  )}

                  <SourceLog entries={detail.data.sourceLog} />
                </div>
              )}
            </section>
          )}

          {scoring && (
            <p
              className="m-0 text-caption text-gray-mid tabular-nums break-words"
              role="status"
              data-testid="research-scoring-progress"
            >
              {copy.scoringProgress(scoring.done, scoring.planned)}
            </p>
          )}

          {showResults && (
            <>
              {/* ABOVE the list, below the account. The account answers "why
                  these listings?" about the SEARCH; Ask Phoenix answers it
                  about the LENS somebody has since put over them — and an
                  applied refinement has to be readable before the list is,
                  because it is what decides which of these cards are on screen.
                  Its history is its own section (RF13) rather than a second
                  kind of row inside the transcript.

                  🔴 That argument governs the applied STATE AND NOTHING ELSE,
                  which is exactly how the owner ruling scopes it: the state
                  stays above the list; the INPUT sits adjacent to the list.
                  WS6 is where that split finally became available, because it
                  is the first change able to edit `AskPhoenixPanel` itself —
                  and the panel now renders NOTHING on a run nobody has
                  narrowed, so the common case costs no rows at all. */}
              {settled && !quick && detail.data.refinement && (
                <AskPhoenixPanel
                  buyerLeadId={buyerLeadId}
                  runId={runId}
                  state={detail.data.refinement}
                  canEdit={canEdit}
                  quote={askPhoenixQuote}
                />
              )}
              <ResearchResults
                buyerLeadId={buyerLeadId}
                runId={runId}
                canEdit={canEdit}
                refinement={detail.data.refinement}
                withheld={detail.data.run.withheld}
                brief={quick ? null : detail.data.brief}
                quick={quick}
                alreadyTiedCount={detail.data.run.alreadyTiedCount}
                skippedAtSearch={detail.data.run.dismissedCount}
                live={liveList}
              />
              {/* 🔴 ADJACENT TO THE LIST IT ACTS ON, which is the owner ruling
                  and the reason decision 6 — which put this in the header — was
                  overturned by its own author's earlier ruling. It sits between
                  the list and the pull control deliberately: it narrows what is
                  on screen, so it sticks to what is on screen, while the pull
                  control asks for MORE and keeps the very foot its own
                  rationale claims. */}
              {settled && !quick && detail.data.refinement && (
                <AskPhoenixInput
                  buyerLeadId={buyerLeadId}
                  runId={runId}
                  state={detail.data.refinement}
                  canEdit={canEdit}
                  quote={askPhoenixQuote}
                />
              )}
              {/* At the FOOT of the list, and a sibling of it rather than part of
                  it: the control's whole state is run-level — the pull decision,
                  the budget, the last batch's figures off the source log — and
                  threading three run-level props into a component about
                  candidates to render it inside would be the worse shape. */}
              {settled && (
                <ResearchPullControl
                  buyerLeadId={buyerLeadId}
                  run={detail.data.run}
                  sourceLog={detail.data.sourceLog}
                  canEdit={canEdit}
                  refinement={detail.data.refinement}
                  onPullStarted={onPullStarted}
                />
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Per-source progress: display name, outcome, candidate count, cost, error.
 *
 * <p>🔴 RB8 — a SKIP is not a fault. With the web-search sources switched off
 * (the default, since Clef's Enterprise API covers the same marketplaces),
 * every one of them skips on every single run; listing them beside the ones
 * that actually ran buries the failures that matter, and the old markup painted
 * their reasons in the same error red it uses for a real failure. They collapse
 * behind one line here, expandable, and a skip reason is muted rather than
 * red. The count is derived, never written down.</p>
 *
 * <p>🔴 <b>The React key carries the POSITION, because a source key is not
 * unique here.</b> A pull appends a SECOND log entry for the same source, so a
 * run that has been pulled renders two rows keyed "clef" — React warns, and can
 * reuse the wrong node. The batch number is not the fix either: it is minted as
 * <c>MAX(candidate.BatchNumber) + 1</c>, so a page whose every row was a
 * duplicate leaves it where it was and two entries share it. The log is an
 * append-only array rendered in order, which makes the index the one thing
 * that is unique by construction.</p>
 *
 * <p>Keyed on the OUTCOME, never on the reason text: FIVE different reasons
 * already flow through that field — the web-sources-disabled one, "No provider
 * is registered for this source.", and Clef's own three (not enabled, inside
 * the re-run window, under the daily-allowance reserve) — and a sixth would go
 * uncollapsed.</p>
 */
function SourceLog({ entries }: { entries: BuyerResearchSourceLogEntryDto[] }) {
  const copy = researchCopy.tab;
  const [skippedOpen, setSkippedOpen] = useState(false);
  if (entries.length === 0) return null;

  const skipped = entries.filter((e) => e.outcome === "skipped");
  const ran = entries.filter((e) => e.outcome !== "skipped");

  // Folded into the summary only when every skipped source gave the SAME
  // reason. One of several reasons cannot speak for the others, so a mixed set
  // leaves the count standing alone and sends the reader to the rows.
  const skipReasons = skipped.map((e) => e.error?.trim() ?? "");
  const sharedReason = skipReasons.every((r) => r !== "" && r === skipReasons[0])
    ? skipReasons[0]
    : null;

  return (
    <section className="grid gap-space-2" data-testid="research-source-log">
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">{copy.sourcesHeading}</h3>
      {ran.length > 0 && (
        <ul className="m-0 p-0 list-none grid gap-space-1">
          {ran.map((e, i) => (
            <SourceRow key={`${e.sourceKey}:${i}`} entry={e} />
          ))}
        </ul>
      )}
      {skipped.length > 0 && (
        <div className="grid gap-space-1" data-testid="research-sources-skipped">
          <div className="flex flex-wrap items-center gap-space-2">
            <span className="text-caption text-gray-mid break-words">
              {copy.skippedSummary(skipped.length, sharedReason)}
            </span>
            <Button
              type="button"
              variant="text"
              size="sm"
              aria-expanded={skippedOpen}
              onClick={() => setSkippedOpen((open) => !open)}
              data-testid="research-sources-skipped-toggle"
            >
              {skippedOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
              {skippedOpen ? copy.skippedHide : copy.skippedShow}
            </Button>
          </div>
          {skippedOpen && (
            <ul className="m-0 p-0 list-none grid gap-space-1">
              {skipped.map((e, i) => (
                <SourceRow key={`${e.sourceKey}:${i}`} entry={e} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * One source's row — shared by the list of sources that ran and the expanded
 * skipped list, so the two cannot drift apart.
 *
 * <p>The reason line is red for a FAILURE and muted for a SKIP. Both arrive on
 * the same `error` field, so the outcome is the only thing that tells them
 * apart.</p>
 */
function SourceRow({ entry: e }: { entry: BuyerResearchSourceLogEntryDto }) {
  const outcomeWord = researchCopy.outcome[e.outcome] ?? e.outcome;
  return (
    <li
      className="flex flex-wrap items-center gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2 text-small"
      data-testid="research-source-row"
      data-outcome={e.outcome}
    >
      <span className="text-onyx">{e.displayName}</span>
      {/* 🔴 SS6.1 — a STATE ABSENT is muted grey prose, never a pill. `empty`
          and `skipped` are the common case here, not the exception: with the
          web sources off by default every one of them skips on every run, and
          both tones rendered the identical grey chip, so the pill was a border
          and 8px of padding around a word that already said it. `succeeded`
          keeps its green and `failed` its red — those are SS6's state-present
          and exception rows, and muting the good outcome would, in this
          module's own words, "paint the best result like a dead one". */}
      {sourceOutcomeIsStateAbsent(e.outcome) ? (
        <span className="text-caption text-gray-mid" data-testid="research-source-outcome">
          {outcomeWord}
        </span>
      ) : (
        <StatusBadge tone={sourceOutcomeTone(e.outcome)} data-testid="research-source-outcome">
          {outcomeWord}
        </StatusBadge>
      )}
      <span className="text-caption text-gray-mid tabular-nums">
        {/* The cost is named only where there IS one. A source that skipped or
            found nothing spent nothing, and `$0.00` down a column of skipped
            rows is the run total restated as a column of zeroes — the same
            `> 0` guard `AskPhoenixHistory` already applies to a refinement's
            cost, for the same reason. The run's own total is stated once, in
            the header. */}
        {e.candidateCount}
        {e.estimatedCostUsd > 0 && <> · {formatCost(e.estimatedCostUsd)}</>} ·{" "}
        {formatDurationMs(e.durationMs)}
      </span>
      {e.error && (
        <span
          className={
            e.outcome === "skipped"
              ? "text-caption text-gray-mid break-words"
              : "text-caption text-status-error break-words"
          }
          data-testid="research-source-reason"
        >
          {e.error}
        </span>
      )}
      {/* 🔴 R1/R15 — rows this source's batch BOUGHT and never scored. They are
          never shown unscored and never offered by the reveal, so this line is
          the only place they surface — for the run's own search (batch 1) as
          much as for a pull, whose control says the same thing in its batch
          line. The sentence is keyed on the server's reason code; one this
          client has no sentence for gets the bare count. */}
      {(e.unscored ?? 0) > 0 && (
        <span className="text-caption text-gray-mid break-words" data-testid="research-source-unscored">
          {researchCopy.pull.unscoredSentence(e.unscored ?? 0, e.unscoredReason)}
        </span>
      )}
    </li>
  );
}
